import styles from "./Confirmation.module.css";
import { useEsCliente } from "../../../../../../shared/hooks/useTenantTipo";
import { useLang } from "../../../../../../shared/i18n/useLang";
import { calcularCambios } from "./cambios";

const COLOR_CAMBIO = {
  agregado:   { color: "#4ade80", icon: "fa-plus",       label: "Agregado" },
  quitado:    { color: "#f87171", icon: "fa-minus",      label: "Quitado" },
  modificado: { color: "#fbbf24", icon: "fa-pen",        label: "Cambio" },
};

// Resumen de Editar: solo lo que cambió respecto a lo que había guardado.
function ResumenCambios({ original, formData }) {
  const cambios = calcularCambios(original, formData);
  const secciones = [...new Set(cambios.map((c) => c.seccion))];
  return (
    <div className="step">
      <div className={`${styles.header} mt-2`}>
        <div className={styles.headerIcon}>
          <i className="fa-solid fa-clipboard-check" style={{ color: "#f97316" }} />
        </div>
        <div>
          <h3 className={styles.title}>Resumen de cambios</h3>
          <p className={styles.subtitle}>Revisá lo que se va a modificar antes de guardar</p>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <i className="fa-solid fa-code-compare" style={{ color: "#f97316" }} />
          <span>{cambios.length} cambio(s) - Filial {formData.filial.idFilial}</span>
        </div>
        {cambios.length === 0 ? (
          <div style={{ padding: "22px 18px", color: "var(--text-muted)", fontSize: "0.88rem", textAlign: "center" }}>
            No hiciste ningún cambio. Podés volver atrás para editar algo.
          </div>
        ) : (
          <div style={{ padding: "6px 18px 14px", maxHeight: "46vh", overflowY: "auto" }}>
            {secciones.map((sec) => (
              <div key={sec} style={{ marginTop: 10 }}>
                <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.6px",
                  textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 6 }}>
                  {sec}
                </div>
                {cambios.filter((c) => c.seccion === sec).map((c, i) => {
                  const cfg = COLOR_CAMBIO[c.tipo];
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10,
                      padding: "6px 0", borderBottom: "1px solid var(--border-card)", fontSize: "0.85rem" }}>
                      <span style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 5,
                        padding: "1px 8px", borderRadius: 999, fontSize: "0.7rem", fontWeight: 700,
                        color: cfg.color, border: `1px solid ${cfg.color}55`, background: `${cfg.color}14` }}>
                        <i className={`fa-solid ${cfg.icon}`} style={{ color: "inherit", fontSize: "0.62rem" }} />
                        {cfg.label}
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ color: "var(--text-primary)" }}>{c.texto}</span>
                        {c.tipo === "modificado" && (
                          <span style={{ display: "block", color: "var(--text-muted)", fontSize: "0.8rem", marginTop: 2 }}>
                            <span style={{ textDecoration: "line-through" }}>{c.antes}</span>
                            {"  →  "}
                            <span style={{ color: "var(--text-primary)" }}>{c.despues}</span>
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={`${styles.notice} d-flex align-items-center`}>
        <i className="fa-solid fa-circle-info" style={{ color:"#60a5fa", flexShrink:0 }} />
        <span>Al guardar se aplican todos estos cambios de una sola vez.</span>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value} title={String(value)}>{value}</span>
    </div>
  );
}

function Section({ icon, title, color = "#f97316", children }) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.iconBox} style={{ background: `${color}14`, border: `1px solid ${color}28` }}>
          <i className={`fa-solid ${icon}`} style={{ color, fontSize: "0.85rem" }} />
        </div>
        <span className={styles.sectionTitle}>{title}</span>
      </div>
      <div className={styles.sectionBody}>{children}</div>
    </div>
  );
}

function CountBadge({ count, label, icon, color }) {
  return (
    <div className={styles.badge}>
      <i className={`fa-solid ${icon}`} style={{ color, fontSize: "0.78rem" }} />
      <span className={styles.badgeCount} style={{ color }}>{count}</span>
      <span className={styles.badgeLabel}>{label}</span>
    </div>
  );
}

export default function Confirmation({ formData, original }) {
  const esCliente = useEsCliente();
  const L = useLang();
  if (original) return <ResumenCambios original={original} formData={formData} />;
  const p = formData.propietario;
  const nombreMostrado = (esCliente
    ? (p.nombre_sociedad || `${p.nombre || ""} ${p.apellidos || ""}`)
    : (p.es_sociedad ? p.nombre_sociedad : `${p.nombre || ""} ${p.apellidos || ""}`)
  ).trim();
  const placas = (p.vehiculos || []).map((v) => (v.placa || "").trim().toUpperCase()).filter(Boolean);
  return (
    <div className="step">

      {/* ── Header ── */}
      <div className={`${styles.header} mt-2`}>
        <div className={styles.headerIcon}>
          <i className="fa-solid fa-clipboard-check" style={{ color: "#f97316" }} />
        </div>
        <div>
          <h3 className={styles.title}>{L("Confirmación y Resumen", "Confirmation & Summary")}</h3>
          <p className={styles.subtitle}>{L("Verificá que los datos sean correctos antes de guardar", "Check that the data is correct before saving")}</p>
        </div>
      </div>

      {/* ── Card ── */}
      <div className={styles.card}>

        {/* Card header */}
        <div className={styles.cardHeader}>
          <i className="fa-solid fa-list-check" style={{ color: "#f97316" }} />
          <span>{L("Resumen de la información", "Information summary")}</span>
        </div>

        <div className={styles.grid}>

          {/* Identificación */}
          <Section icon="fa-house" title={esCliente ? L("Cliente", "Client") : L("Filial", "Unit")} color="#f97316">
            <InfoRow label={esCliente ? L("ID / Código", "ID / Code") : "ID Filial"} value={formData.filial.idFilial} />
            {!esCliente && <InfoRow label="N° Plano"          value={formData.filial.numPlano} />}
            {!esCliente && <InfoRow label="N° Folio"          value={formData.filial.numFolio} />}
            {!esCliente && <InfoRow label="Tipo de propiedad" value={formData.filial.tipoPropiedad} />}
          </Section>

          {/* Datos de la persona/cliente */}
          <Section icon="fa-user" title={esCliente ? L("Datos", "Details") : L("Propietario", "Owner")} color="#60a5fa">
            <InfoRow label={L("Nombre", "Name")}          value={nombreMostrado} />
            <InfoRow label={L("Email principal", "Primary email")}
              value={p.sin_correo ? L("No tiene correo", "No email") : p.emailPrincipal} />
            {!esCliente && <InfoRow label={L("Placas", "Plates")} value={placas.join(", ")} />}
          </Section>

        </div>

        {/* Counts — solo en modo condominio */}
        {!esCliente && (
          <div className={styles.counts}>
            <CountBadge count={formData.copropietarios.length}
              label="copropietario(s)" icon="fa-users" color="#a78bfa" />
            <span className={styles.countDivider} />
            <CountBadge count={formData.autorizados.length}
              label="autorizado(s)" icon="fa-id-card" color="#4ade80" />
          </div>
        )}

      </div>

      {/* Notice */}
      <div className={`${styles.notice} d-flex align-items-center`}>
        <i className="fa-solid fa-circle-info" style={{ color:"#60a5fa", flexShrink:0 }} />
        <span>{L(`Al confirmar se creará el ${esCliente ? "cliente" : "residente"} y todos sus datos asociados.`,
                 `Confirming will create the ${esCliente ? "client" : "resident"} and all associated data.`)}</span>
      </div>

    </div>
  );
}
